/**
 * @fileoverview Modal de visualização e edição de produto para administradores.
 * Modo visualização: todos os campos exibidos, nenhum editável.
 * Modo edição: apenas o campo status (ativo/inativo) é editável.
 *
 * @module ProductEditModal
 * @since 1.0.0
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Modal de visualização e edição de produto.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Controla visibilidade do modal
 * @param {Function} props.onClose - Callback ao fechar modal
 * @param {Object} props.product - Dados do produto
 * @param {'view'|'edit'} props.mode - Modo de operação
 * @param {Function} props.onSave - Callback ao salvar (apenas no modo edit)
 * @returns {JSX.Element}
 */
export default function ProductEditModal({ open, onClose, product, mode = 'view', onSave }) {
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (product) {
      setActive(product.ativo !== false && product.ativo !== 0);
      setError(null);
    }
  }, [product]);

  /**
   * Determina o label e cor do status do produto.
   * @param {Object} p - Produto
   * @returns {{ label: string, color: string }}
   */
  const getStatusChip = (p) => {
    if (!p) return { label: '-', color: 'default' };
    if (p.removido === true || p.removido === 1) return { label: 'Removido', color: 'error' };
    if (p.ativo === false || p.ativo === 0) return { label: 'Inativo', color: 'warning' };
    return { label: 'Ativo', color: 'success' };
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      await onSave({ id: product.id, active });
      onClose();
    } catch (err) {
      setError(err.message || 'Erro ao atualizar produto');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!product) return null;

  const statusChip = getStatusChip(product);
  const isEdit = mode === 'edit';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Editar Produto' : 'Visualizar Produto'}
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Imagem */}
        {product.imagem && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              component="img"
              src={product.imagem}
              alt={product.nome}
              sx={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 1, bgcolor: 'grey.100', p: 1 }}
            />
          </Box>
        )}

        <TextField label="ID" value={product.id || ''} fullWidth disabled sx={{ mb: 2 }} size="small" />
        <TextField label="Código" value={product.codigo || ''} fullWidth disabled sx={{ mb: 2 }} size="small" />
        <TextField label="Nome" value={product.nome || ''} fullWidth disabled sx={{ mb: 2 }} size="small" />
        <TextField
          label="Descrição"
          value={product.descricao || ''}
          fullWidth
          disabled
          multiline
          rows={3}
          sx={{ mb: 2 }}
          size="small"
        />
        <TextField label="Categoria" value={product.categoria || ''} fullWidth disabled sx={{ mb: 2 }} size="small" />
        <TextField label="Marca" value={product.marca || ''} fullWidth disabled sx={{ mb: 2 }} size="small" />
        <TextField label="Preço" value={product.preco != null ? `R$ ${Number(product.preco).toFixed(2)}` : ''} fullWidth disabled sx={{ mb: 2 }} size="small" />
        <TextField label="Estoque" value={product.quantidade_estoque != null ? Number(product.quantidade_estoque) : ''} fullWidth disabled sx={{ mb: 2 }} size="small" />
        <TextField label="Unidade" value={product.unidade || ''} fullWidth disabled sx={{ mb: 2 }} size="small" />

        <Divider sx={{ my: 2 }} />

        {/* Status atual */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">Status atual:</Typography>
          <Chip label={statusChip.label} color={statusChip.color} size="small" />
        </Box>

        {/* Campo editável apenas no modo edit e se não estiver removido */}
        {isEdit && (product.removido !== true && product.removido !== 1) && (
          <FormControlLabel
            control={
              <Checkbox
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label="Produto Ativo"
          />
        )}

        {isEdit && (product.removido === true || product.removido === 1) && (
          <Alert severity="warning">
            Produtos removidos não podem ter o status alterado.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {isEdit ? 'Cancelar' : 'Fechar'}
        </Button>
        {isEdit && (product.removido !== true && product.removido !== 1) && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            Atualizar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
